import React, { useEffect, useMemo, useState } from "react";
import CreditsPage from "../../entities/Credits";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

const oneEmloyeeData = []
const Credits = () => {
  const { showAlert } = useAlert();
  const [taxPeriod, setTaxPeriod] = useState();
  const [loading, setLoading] = useState(false);
  const [creditsType, setCreditsType] = useState([]);
  const [year, setYear] = useState();
  const [frequency, setFrequency] = useState("Monthly");
  const [credits, setCredits] = useState([])
  const [data, setData] = useState();
  const [success, setSuccess] = useState(null);
  const location = useLocation();
  const { taxCountry } = location.state || {};
  const [contributionType, setContributionsType] = useState([])

  useEffect(() => {
    getCreditsType();
    getTaxPeriod();
    getContributionsType();
  }, []);

  const getContributionsType = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAdminContributionTypes();
      if (result?.success) {
        setContributionsType(result?.data?.contributions || []);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };


  const handleAddTaxYear = async (start, end) => {
    setLoading(true);
    try {
      const result = await AuthService.handleAddTaxPeriod(start, end);
      if (result?.success === true) {
        getTaxPeriod();
        setLoading(false);
        showAlert(result?.message, "success");
      } else {
        setLoading(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false);
      showAlert(error?.message, "error");
    }
  };

  const getTaxPeriod = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getTaxPeriod(taxCountry);
      if (result?.success) {
        setTaxPeriod(result?.data);
        const currentYear = new Date().getFullYear();
        const matchedYear = result?.data?.find((item) => {
          const startYear = dayjs(item?.start).year();
          return startYear === currentYear;
        });
        setYear(matchedYear ? matchedYear._id : result?.data?.[0]?._id || null);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getCreditsType = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getCreditsType(taxCountry);
      if (result?.success) {
        setCreditsType(result?.data?.credits || []);
      }
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getCreditsTypeByDateRange = async (country, start, end) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getCreditsTypeByDateRange(country, start, end);
      if (result?.success) {
        const creditTypes = result?.data?.credits || result?.data || [];
        setCreditsType(creditTypes);
        return creditTypes;
      } else {
        setCreditsType([]);
        if (result?.message) {
          showAlert(result.message, "info");
        }
        return [];
      }
    } catch (error) {
      setCreditsType([]);
      showAlert(error?.message || "Error fetching credit types", "error");
      return [];
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleAddCreditType = async (creditTypeName, country, includeBenificiary, contribution, contributionTypeId, start, end, frequency) => {
    setLoading(true);
    try {
      const result = await AuthService.addCreditsType(
        creditTypeName,
        country,
        includeBenificiary,
        contribution,
        contributionTypeId,
        start,
        end,
        frequency
      );
      if (result?.success === true) {
        showAlert(result?.message, "success");
        await getCreditsType();
      } else {
        showAlert(result?.message, "error");
      }
      return result;
    } catch (error) {
      showAlert(error?.message || "Something went wrong", "error");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCreditType = async (
    creditTypeName,
    country,
    contribution,
    contributionType,
    name
  ) => {
    setLoading(true);
    try {
      let creditTypeId, creditCountry, creditContribution, creditContributionType, creditName;

      if (typeof creditTypeName === 'object' && creditTypeName !== null) {
        creditTypeId = creditTypeName._id;
        creditCountry = creditTypeName.country || country;
        creditContribution = creditTypeName.contribution || contribution;
        creditContributionType = creditTypeName.contributionType || contributionType;
        creditName = creditTypeName.name || creditTypeName.type || name;
      } else {
        const foundType = creditsType.find(ct => ct.name === creditTypeName || ct._id === creditTypeName);
        creditTypeId = foundType?._id || creditTypeName;
        creditCountry = country;
        creditContribution = contribution;
        creditContributionType = contributionType;
        creditName = name || foundType?.name;
      }

      const result = await AuthService.updateCreditsType(
        creditTypeId,
        creditCountry,
        creditContribution,
        creditContributionType,
        creditName
      );
      if (result?.success === true) {
        showAlert(result?.message, "success");
        getCreditsType();
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteCreditType = async (creditTypeId, onSuccess) => {
    setLoading(true);
    try {
      const result = await AuthService.deleteCreditsType(creditTypeId);
      if (result?.success === true) {
        showAlert(result?.message, "success");
        await getCreditsType();
        if (onSuccess) onSuccess();
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  }

  const handleCredits = async (data) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.addEmployeeCredits(data);
      if (result?.success === true) {
        showAlert(result?.message, "success");
      } else {
        showAlert(result?.message || "Failed to create credit.", "error");
      }
      return result;
    } catch (error) {
      showAlert(error?.message || "Failed to create credit.", "error");
      throw error;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleUpdateCreditsData = async (data, id) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.updateEmployeeCredits(data, id);
      if (result?.success === true) {
        showAlert(result?.message, "success");
      } else {
        showAlert(result?.message || "Failed to update credit.", "error");
      }
      return result;
    } catch (error) {
      showAlert(error?.message || "Failed to update credit.", "error");
      throw error;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getCredits = async (start, end, type) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAllCredits(start, end, type);
      if (result?.success) {
        setCredits(result?.data?.credits || []);

      }
      else {
        setCredits([]);
        showAlert("Failed to fetch credits", "error");
      }
    } catch (error) {
      setCredits([]);
      showAlert("Error fetching credits", "error");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getYearTaxTable = async (id, country) => {
    if (!id || !country) {
      return;
    }

    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getYearTaxTable(id, country);

      if (result?.success) {
        if (result?.data) {
          setData(result?.data);
        }
        setSuccess(result?.success);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message || "Failed to fetch tax table", "error");
        setSuccess(result?.success);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error fetching tax table", "error");
    }
  };


  const handleFrequencyChange = async (newFrequency) => {
    setFrequency(newFrequency);
  };

  const handleUpdateCredits = async (data, id) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.updateCredits(data, id);
      if (result?.success) {
        showAlert(result?.message, "success");
      } else {
        showAlert(result?.message || "Failed to update credit.", "error");
        setSuccess(result?.success);
      }
      return result;
    } catch (error) {
      showAlert(error?.message || "Failed to update credit.", "error");
      throw error;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getAllCreditsList = async () => {
    await getCreditsType();
  };

  const deleteCreditById = async (creditId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteCreditsType(creditId);
      if (result?.success === true) {
        showAlert(result?.message || "Credit deleted successfully", "success");
        await getCreditsType();
      } else {
        showAlert(result?.message || "Failed to delete credit", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Something went wrong", "error");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePostCreditBeneficiaries = async (data, id) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.postCreditBeneficiaries(data, id);
      if (result?.success === true) {
        showAlert(result?.message || (id ? "Beneficiaries updated successfully" : "Beneficiaries saved successfully"), "success");
      } else {
        showAlert(result?.message || "Failed to save beneficiaries", "error");
      }
      return result;
    } catch (error) {
      showAlert(error?.message || "Failed to save beneficiaries", "error");
      throw error;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleGetCreditBeneficiaries = async (creditTypeId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getCreditBeneficiaries(creditTypeId);
      if (result?.success) {
        return result?.data || null;
      } else {
        showAlert(result?.message || "Failed to fetch beneficiaries", "error");
        return null;
      }
    } catch (error) {
      showAlert(error?.message || "Error fetching beneficiaries", "error");
      return null;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <CreditsPage
      loading={loading}
      creditsType={creditsType}
      handleAddCreditType={handleAddCreditType}
      updateCreditType={updateCreditType}
      deleteCreditType={deleteCreditType}
      taxPeriod={taxPeriod}
      handleFrequencyChange={handleFrequencyChange}
      credits={credits}
      handleCreditsData={handleCredits}
      oneEmloyeeData={oneEmloyeeData}
      data={data}
      success={success}
      taxCountry={taxCountry}
      getYearTaxTable={getYearTaxTable}
      frequency={frequency}
      handleAddTaxYear={handleAddTaxYear}
      handleUpdateCreditsData={handleUpdateCreditsData}
      getCredits={getCredits}
      contributionType={contributionType}
      handleUpdateCredits={handleUpdateCredits}
      getAllCreditsList={getAllCreditsList}
      deleteCreditById={deleteCreditById}
      handlePostCreditBeneficiaries={handlePostCreditBeneficiaries}
      handleGetCreditBeneficiaries={handleGetCreditBeneficiaries}
      getCreditsTypeByDateRange={getCreditsTypeByDateRange}
    />
  );
};

export default Credits;


